// =============================================================================
// ui/share/SharedLinksCard.tsx — the links you have sent, and how to kill one.
// -----------------------------------------------------------------------------
// ⚠ IT SHOWS REVOKED AND EXPIRED LINKS TOO, greyed. A list that hid them would
// answer "is that link I sent in March still live?" with silence — which is the
// question this list exists to answer. Seeing a row marked *Revoked* is also the
// only confirmation that the click worked.
//
// ⚠ IT DELIBERATELY DOES NOT SHOW THE LINK ITSELF. The token IS the password;
// re-displaying it invites copying an old link back out of the one screen that
// exists to retire them. The date and the report name identify a row.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { loadShareLinks, revokeShare, type ShareLinkRow } from '../../selectors/share';
import { useSupabase } from '../state/supabase';
import { reportFailure } from '../../io/diagnostics/errorLog';

type Phase = 'loading' | 'ready' | 'failed';

export function SharedLinksCard() {
  const { sb } = useSupabase();
  const [phase, setPhase] = useState<Phase>('loading');
  const [rows, setRows] = useState<ShareLinkRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    setPhase('loading');
    void (async () => {
      try {
        const r = await loadShareLinks(sb);
        if (alive) { setRows(r); setPhase('ready'); }
      } catch (e) {
        if (!alive) return;
        // Through the funnel, so a failed read reaches the activity log rather
        // than living only on a card the user is about to navigate away from.
        setError(reportFailure('share links', e));
        setPhase('failed');
      }
    })();
    return () => { alive = false; };
  }, [sb]);

  useEffect(load, [load]);

  const revoke = (token: string) => {
    setBusy(token);
    void (async () => {
      try {
        await revokeShare(sb, token);
        setRows((prev) => prev.map((r) => (
          r.token === token ? { ...r, revokedAt: new Date().toISOString() } : r
        )));
        setConfirming(null);
      } catch (e) {
        setError(reportFailure('revoke share link', e));
      } finally {
        setBusy(null);
      }
    })();
  };

  const now = Date.now();

  return (
    <section className="card">
      <h3>Shared links</h3>
      <p className="muted share-links-lede">
        Read-only links to a report. Anyone holding one can open it without
        signing in, so treat a link like a password — and revoke it when the
        conversation is over.
      </p>

      {phase === 'loading' ? <p className="muted">Loading your share links…</p> : null}

      {/* ⚠ NAMES THE FAILURE, AND FOR BOTH FAILURES. "No share links yet" on a
          failed READ would tell the owner nothing is out there while their links
          keep serving — and a failed REVOKE that said nothing would be worse
          still: they would walk away believing a link is dead while it is
          serving. This rendered only on the read failure until a test asked for
          the other one. */}
      {error ? (
        <p className="red" role="alert">
          {phase === 'failed'
            ? `Could not load your share links: ${error}`
            : `That link was NOT revoked and is still live: ${error}`}
        </p>
      ) : null}

      {phase === 'ready' && !rows.length ? (
        <p className="muted">No share links yet.</p>
      ) : null}

      {phase === 'ready' && rows.length ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Created</th><th>Report</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const revoked = !!r.revokedAt;
                const expired = !revoked && !!r.expiresAt && Date.parse(r.expiresAt) <= now;
                const dead = revoked || expired;
                const status = revoked ? 'Revoked'
                  : expired ? 'Expired'
                  : r.expiresAt ? `Live · until ${new Date(r.expiresAt).toLocaleString()}`
                  : 'Live · never expires';
                return (
                  <tr key={r.token} className={dead ? 'share-link-dead' : ''}>
                    <td>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
                    <td>
                      {r.title}
                      {r.subtitle ? <span className="muted"> · {r.subtitle}</span> : null}
                    </td>
                    <td className={dead ? 'muted' : ''}>{status}</td>
                    <td>
                      {dead ? null : confirming === r.token ? (
                        /* ⚠ CONFIRMED, because it cannot be undone from here and
                           whoever is holding the link loses access the moment it
                           lands. */
                        <span className="share-link-confirm">
                          <span className="muted">Anyone holding it loses access.</span>
                          <button
                            type="button" className="btn danger btn-sm"
                            disabled={busy === r.token}
                            onClick={() => revoke(r.token)}
                          >{busy === r.token ? 'Revoking…' : 'Revoke it'}</button>
                          <button
                            type="button" className="btn btn-sm"
                            disabled={busy === r.token}
                            onClick={() => setConfirming(null)}
                          >Keep</button>
                        </span>
                      ) : (
                        <button
                          type="button" className="btn danger btn-sm"
                          onClick={() => setConfirming(r.token)}
                        >Revoke</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
